import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Store, Coins, DollarSign, Package, Trash2, ArrowLeft, Copy, Check, ShoppingBag, TrendingUp, Rocket } from 'lucide-react';

export default function MarketplaceMine() {
  const nav = useNavigate();
  const [listings, setListings] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [earnings, setEarnings] = useState({ sales: 0, earnings_jxp: 0, earnings_usd: 0 });
  const [copiedId, setCopiedId] = useState(null);
  const [boostPrice, setBoostPrice] = useState(500);
  const [boostingId, setBoostingId] = useState(null);

  const load = async () => {
    const [l, p, e, pub] = await Promise.all([
      api.get('/marketplace/mine/listings'),
      api.get('/marketplace/mine/purchases'),
      api.get('/marketplace/mine/earnings'),
      api.get('/settings/public').catch(() => ({ data: {} })),
    ]);
    setListings(l.data); setPurchases(p.data); setEarnings(e.data);
    const mp = pub.data?.boost_prices?.marketplace_24h;
    if (mp) setBoostPrice(mp);
  };
  useEffect(() => { load(); }, []);

  const unlist = async (id) => {
    if (!window.confirm('Remove this listing? Existing purchases keep their access.')) return;
    try { await api.delete(`/marketplace/products/${id}`); toast.success('Removed'); load(); }
    catch { toast.error('Failed'); }
  };

  const boostListing = async (id) => {
    if (!window.confirm(`Boost this listing to the top of the Marketplace for 24h for ${boostPrice} JXP?`)) return;
    setBoostingId(id);
    try {
      const { data } = await api.post(`/marketplace/products/${id}/boost`);
      const until = new Date(data.boosted_until).toLocaleString();
      toast.success(`Boosted! Trending until ${until}`);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Boost failed';
      toast.error(detail);
    } finally {
      setBoostingId(null);
    }
  };

  const copyContent = async (text, id) => {
    try { await navigator.clipboard.writeText(text || ''); setCopiedId(id); setTimeout(() => setCopiedId(null), 1500); }
    catch { /* noop */ }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-4 pb-24">
      <header className="flex items-center gap-2">
        <Link to="/marketplace" data-testid="back-to-market">
          <Button variant="outline" size="sm"><ArrowLeft className="w-3.5 h-3.5" /></Button>
        </Link>
        <div>
          <h1 className="font-display text-2xl font-black">My Marketplace</h1>
          <p className="text-xs text-slate-400">Manage your listings and view your purchases.</p>
        </div>
      </header>

      {/* Earnings summary */}
      <div className="grid grid-cols-3 gap-2" data-testid="mine-earnings-grid">
        <Card className="glass p-3">
          <div className="text-[10px] uppercase text-slate-500 flex items-center gap-1"><ShoppingBag className="w-3 h-3" /> Sales</div>
          <div className="text-2xl font-black">{earnings.sales}</div>
        </Card>
        <Card className="glass p-3">
          <div className="text-[10px] uppercase text-slate-500 flex items-center gap-1"><Coins className="w-3 h-3" /> JXP earned</div>
          <div className="text-2xl font-black text-amber-300">{earnings.earnings_jxp.toLocaleString()}</div>
        </Card>
        <Card className="glass p-3">
          <div className="text-[10px] uppercase text-slate-500 flex items-center gap-1"><DollarSign className="w-3 h-3" /> USD earned</div>
          <div className="text-2xl font-black text-emerald-300">${earnings.earnings_usd.toFixed(2)}</div>
        </Card>
      </div>

      <Tabs defaultValue="listings">
        <TabsList className="glass">
          <TabsTrigger value="listings" data-testid="mine-tab-listings"><Package className="w-3.5 h-3.5 mr-1" /> My Listings</TabsTrigger>
          <TabsTrigger value="purchases" data-testid="mine-tab-purchases"><ShoppingBag className="w-3.5 h-3.5 mr-1" /> My Purchases</TabsTrigger>
        </TabsList>

        <TabsContent value="listings" className="mt-4 space-y-2">
          {listings.length === 0 && (
            <Card className="glass p-10 text-center text-sm text-slate-500">
              You haven't listed anything yet. Head back to the marketplace and click "Sell Something".
            </Card>
          )}
          {listings.map(p => (
            <Card key={p.id} className="glass p-3 flex items-center gap-3" data-testid={`mine-listing-${p.id}`}>
              <div className="w-16 h-16 rounded-lg bg-slate-800 overflow-hidden flex-shrink-0">
                {p.preview_url && <img src={p.preview_url} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold truncate flex items-center gap-2">
                  <span className="truncate">{p.title}</span>
                  {p.is_boosted && (
                    <span data-testid={`mine-boosted-${p.id}`}
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/20 border border-amber-500/40 text-amber-200 flex-shrink-0">
                      <Rocket className="w-2.5 h-2.5" /> Boosted
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-slate-500 flex gap-2 flex-wrap">
                  <span>{p.category.replace('_',' ')}</span>
                  <span>·</span>
                  <span>{p.currency === 'usd' ? `$${p.price_usd?.toFixed(2)}` : `${p.price_jxp} JXP`}</span>
                  <span>·</span>
                  <span>{p.sales || 0} sold</span>
                  {!p.active && <><span>·</span><span className="text-rose-300">unlisted</span></>}
                </div>
                <div className="text-[11px] text-slate-400">
                  Earned: {p.earnings_jxp > 0 && <span className="text-amber-300">{p.earnings_jxp} JXP</span>}
                  {p.earnings_jxp > 0 && p.earnings_usd > 0 && <span> · </span>}
                  {p.earnings_usd > 0 && <span className="text-emerald-300">${p.earnings_usd.toFixed(2)}</span>}
                  {!p.earnings_jxp && !p.earnings_usd && <span className="text-slate-500">No sales yet</span>}
                </div>
              </div>
              {p.active && (
                <div className="flex flex-col gap-1 flex-shrink-0">
                  <Button
                    data-testid={`mine-boost-${p.id}`}
                    size="sm"
                    disabled={boostingId === p.id}
                    onClick={() => boostListing(p.id)}
                    className={`${p.is_boosted ? 'bg-amber-500/80 hover:bg-amber-500' : 'gradient-brand'} text-white text-[11px] px-2 py-1 h-7`}
                    title={`Boost 24h for ${boostPrice} JXP`}
                  >
                    <Rocket className="w-3 h-3 mr-1" />
                    {boostingId === p.id ? '…' : p.is_boosted ? '+24h' : `Boost`}
                  </Button>
                  <Button data-testid={`mine-unlist-${p.id}`} size="sm" variant="outline" className="h-7 px-2" onClick={() => unlist(p.id)}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              )}
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="purchases" className="mt-4 space-y-2">
          {purchases.length === 0 && (
            <Card className="glass p-10 text-center text-sm text-slate-500">
              You haven't bought anything yet.
            </Card>
          )}
          {purchases.map(o => (
            <Card key={o.id} className="glass p-3" data-testid={`mine-purchase-${o.id}`}>
              <div className="flex items-center gap-3">
                <div className="w-16 h-16 rounded-lg bg-slate-800 overflow-hidden flex-shrink-0">
                  {o.product?.preview_url && <img src={o.product.preview_url} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold truncate">{o.product?.title || o.title || 'Removed listing'}</div>
                  <div className="text-[11px] text-slate-500">
                    {o.product?.category?.replace('_',' ')} · by @{o.product?.seller_username || '?'} · {new Date(o.created_at).toLocaleDateString()}
                  </div>
                  <div className="text-[11px]">
                    Paid: {o.currency === 'usd'
                      ? <span className="text-emerald-300">${((o.amount_usd_cents || 0) / 100).toFixed(2)}</span>
                      : <span className="text-amber-300">{o.amount_jxp} JXP</span>}
                  </div>
                </div>
                {o.product?.content && (
                  <Button data-testid={`mine-copy-${o.id}`} size="sm" variant="outline" onClick={() => copyContent(o.product.content, o.id)}>
                    {copiedId === o.id ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  </Button>
                )}
              </div>
              {o.product?.content && (
                <pre className="mt-2 text-[11px] whitespace-pre-wrap text-slate-300 max-h-32 overflow-y-auto bg-slate-900/50 p-2 rounded">{o.product.content}</pre>
              )}
            </Card>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
